import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  QUOTATION_STATUS_DEFS,
  QUOTATION_STATUS_MAP,
  type QuotationStatus,
} from "@goyone/shared";
import { api, qs } from "../../lib/api";
import { useDebounced, useListState } from "../../lib/hooks";
import { date, money, plural, WORDS } from "../../lib/format";
import { Icon } from "../../components/Icon";
import { DocPreview } from "../../components/DocPreview";
import { RecordStats, type RecordStat } from "../../components/RecordStats";
import { CardPickerButton, useCardChoice } from "../../components/CardPicker";
import {
  ColumnPickerButton,
  useColumnChoice,
  useColumnDrag,
  type ColumnDef,
} from "../../components/ColumnPicker";
import {
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from "../../components/ColumnFilter";
import { QuotationActions } from "./QuotationActions";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../components/Toast";
import {
  Badge,
  EmptyState,
  ErrorState,
  PageHead,
  Pager,
  SearchBox,
  TableSkeleton,
} from "../../components/ui";

interface QuotationRow {
  id: string;
  number: string;
  status: QuotationStatus;
  title: string | null;
  total: string;
  issuedAt: string;
  validUntil: string | null;
  account: { id: string; name: string } | null;
  lead?: { id: string; name: string } | null;
}

/** كتالوج أعمدة عروض الأسعار. الرقم ثابتٌ: هو ما يُعرف به العرض. */
const QUOTATION_COLUMNS: readonly ColumnDef[] = [
  { key: "number", label: "الرقم", fixed: true },
  { key: "account", label: "الحساب" },
  { key: "title", label: "العنوان" },
  { key: "status", label: "الحالة" },
  { key: "total", label: "الإجمالي", group: "الأرقام" },
  { key: "issuedAt", label: "التاريخ", group: "التواريخ" },
  { key: "validUntil", label: "الصلاحية", group: "التواريخ" },
];

/** أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في quotations.service.ts. */
const QUOTATION_FILTERS: FilterDef[] = [
  { key: "number", label: "الرقم", kind: "text", sortable: true },
  { key: "account", label: "الحساب", kind: "text", noList: true },
  { key: "title", label: "العنوان", kind: "text" },
  {
    key: "status",
    label: "الحالة",
    kind: "enum",
    options: QUOTATION_STATUS_DEFS.map((s) => ({ value: s.key, label: s.label })),
  },
  { key: "total", label: "الإجمالي", kind: "number", sortable: true },
  { key: "issuedAt", label: "التاريخ", kind: "date", sortable: true },
  { key: "validUntil", label: "الصلاحية", kind: "date", sortable: true },
];

const QUOTATION_NUMERIC = new Set(["total"]);

interface QuotationPage {
  items: QuotationRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export function QuotationsList() {
  const navigate = useNavigate();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [previewing, setPreviewing] = useState<QuotationRow | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const columns = useMemo<ColumnDef[]>(() => [...QUOTATION_COLUMNS], []);
  const {
    visible: shownColumns,
    state: columnState,
    setState: setColumnState,
    reset: resetColumns,
    touched: columnsTouched,
  } = useColumnChoice("goyone.columns.quotations", columns);
  const colDrag = useColumnDrag(columnState, setColumnState);
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState(state.q ?? "");
  const debounced = useDebounced(search);

  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ["quotations", query],
    queryFn: () => api<QuotationPage>(`/sales/quotations${qs(query)}`),
  });

  const now = Date.now();

  /* حذف المحدد: لا مسار جماعيّ في الخادم، فيُحذف واحدًا واحدًا ويُبلَّغ العدد. */
  const bulkDelete = useMutation({
    mutationFn: async (ids: string[]) => {
      let ok = 0;
      for (const id of ids) {
        try {
          await api(`/sales/quotations/${id}`, { method: "DELETE" });
          ok += 1;
        } catch {
          /* عرضٌ مقبولٌ لا يُحذف — يُتخطّى ويُحصى الباقي. */
        }
      }
      return ok;
    },
    onSuccess: (ok) => {
      toast(`حُذف ${ok} من ${picked.size}`);
      setPicked(new Set());
      void queryClient.invalidateQueries({ queryKey: ["quotations"] });
    },
  });

  const pageIds = (list.data?.items ?? []).map((q) => q.id);

  const cardChoice = useCardChoice("goyone.stats.quotations", [
    "count",
    "value",
    "sent",
    "accepted",
  ]);
  const statItems = list.data ? quotationCards(list.data) : [];
  const statChoices = statItems.map(({ key, label, group }) => ({
    key,
    label,
    group,
  }));

  return (
    <div className="page">
      <PageHead
        title="عروض الأسعار"
        subtitle={
          list.data ? plural(list.data.total, WORDS.quotation) : undefined
        }
      />

      {list.data && (
        <RecordStats
          items={statItems}
          storageKey="goyone.stats.quotations"
          defaults={["count", "value", "sent", "accepted"]}
          external={cardChoice}
        />
      )}

      {picked.size > 0 && (
        <div className="bulk-bar">
          <span className="strong">{picked.size} محدَّد</span>
          <button className="btn btn-sm" onClick={() => setPicked(new Set())}>
            إلغاء التحديد
          </button>
          <button
            className="btn btn-sm btn-danger"
            style={{ marginInlineStart: "auto" }}
            disabled={bulkDelete.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `حذف ${picked.size} عرضًا؟ العروض المقبولة تُتخطّى.`,
                )
              )
                bulkDelete.mutate([...picked]);
            }}
          >
            <Icon name="trash" size={14} />
            حذف المحدَّد
          </button>
        </div>
      )}

      {/* ترتيب الشريط كقوائم إدارة العملاء: بحثٌ فمرشّحات، والاختيارات والإضافة في طرفه. */}
      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث برقم العرض أو عنوانه"
        />

        <select
          className="select"
          style={{ width: "auto" }}
          value={state.status ?? ""}
          onChange={(e) => patch({ status: e.target.value || undefined })}
          aria-label="الحالة"
        >
          <option value="">كل الحالات</option>
          {QUOTATION_STATUS_DEFS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>

        <span
          className="row"
          style={{ marginInlineStart: "auto", gap: "var(--sp-2)" }}
        >
          <FilterButton
            defs={QUOTATION_FILTERS}
            filters={filters}
            onChange={setFilters}
          />
          <ColumnPickerButton
            columns={columns}
            state={columnState}
            onChange={setColumnState}
            onReset={resetColumns}
            touched={columnsTouched}
          />
          <CardPickerButton
            choices={statChoices}
            chosen={cardChoice.chosen}
            onChange={cardChoice.setChosen}
            onReset={cardChoice.reset}
          />
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate("/sales/quotations/new")}
          >
            <Icon name="plus" size={14} />
            عرض جديد
          </button>
        </span>
      </div>

      <FilterChips
        defs={QUOTATION_FILTERS}
        filters={filters}
        onChange={setFilters}
      />

      <FilterScope
        defs={QUOTATION_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/sales/quotations/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th className="col-act" />
                {shownColumns.map((c) => (
                  <th
                    key={c.key}
                    {...colDrag.thProps(
                      c,
                      QUOTATION_NUMERIC.has(c.key) ? "col-num" : undefined,
                    )}
                  >
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
                <th className="col-pick">
                  <input
                    type="checkbox"
                    aria-label="تحديد كل الصفحة"
                    checked={
                      pageIds.length > 0 &&
                      pageIds.every((id) => picked.has(id))
                    }
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          pageIds.some((id) => picked.has(id)) &&
                          !pageIds.every((id) => picked.has(id));
                    }}
                    onChange={(e) => {
                      const next = new Set(picked);
                      if (e.target.checked)
                        pageIds.forEach((id) => next.add(id));
                      else pageIds.forEach((id) => next.delete(id));
                      setPicked(next);
                    }}
                  />
                </th>
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={shownColumns.length + 2} />}

            {list.data && (
              <tbody>
                {list.data.items.map((q) => {
                  const expiring =
                    q.status === "SENT" &&
                    q.validUntil &&
                    new Date(q.validUntil).getTime() - now < 3 * 86_400_000;
                  return (
                    <tr key={q.id}>
                      <td className="col-act">
                        <QuotationActions
                          quotation={q}
                          onPreview={() => setPreviewing(q)}
                          onChanged={() => void list.refetch()}
                        />
                      </td>
                      {shownColumns.map((c) => (
                        <QuotationCell
                          key={c.key}
                          column={c}
                          quotation={q}
                          expiring={Boolean(expiring)}
                        />
                      ))}
                      <td className="col-pick">
                        <input
                          type="checkbox"
                          aria-label={`تحديد العرض ${q.number}`}
                          checked={picked.has(q.id)}
                          onChange={(e) => {
                            const next = new Set(picked);
                            if (e.target.checked) next.add(q.id);
                            else next.delete(q.id);
                            setPicked(next);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        )}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا توجد عروض أسعار"
            text="أنشئ عرضًا من حساب عميل، أو مباشرة من هنا."
            action={
              <button
                className="btn btn-primary"
                onClick={() => navigate("/sales/quotations/new")}
              >
                عرض جديد
              </button>
            }
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
          />
        )}
      </div>
      </FilterScope>

      {previewing && (
        <DocPreview
          path={`/print/quotation/${previewing.id}`}
          title={`عرض سعر ${previewing.number}`}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}

/** خليّةٌ واحدة، يُختار شكلها بمفتاح العمود لا بموضعه. */
function QuotationCell({
  column,
  quotation: q,
  expiring,
}: {
  column: ColumnDef;
  quotation: QuotationRow;
  expiring: boolean;
}) {
  switch (column.key) {
    case "number":
      return <td className="cell-strong num">{q.number}</td>;

    case "account":
      return (
        <td>
          {q.account ? (
            <Link
              to={`/crm/accounts/${q.account.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {q.account.name}
            </Link>
          ) : q.lead ? (
            <Link
              to={`/crm/leads/${q.lead.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {q.lead.name} <span className="tiny mute">محتمل</span>
            </Link>
          ) : (
            "—"
          )}
        </td>
      );

    case "title":
      return <td className="cell-mute">{q.title ?? "—"}</td>;

    case "status":
      return (
        <td>
          <Badge kind={QUOTATION_STATUS_MAP[q.status]?.kind}>
            {QUOTATION_STATUS_MAP[q.status]?.label}
          </Badge>
        </td>
      );

    case "total":
      return <td className="num">{money(q.total)}</td>;

    case "issuedAt":
      return <td className="cell-mute">{date(q.issuedAt)}</td>;

    case "validUntil":
      /* المشارف على الانتهاء بلونٍ محذّر: هو ما يحتاج تجديدًا أو متابعة. */
      return (
        <td className={expiring ? "" : "cell-mute"}>
          <span style={{ color: expiring ? "var(--warning)" : undefined }}>
            {date(q.validUntil)}
          </span>
        </td>
      );

    default:
      return <td>—</td>;
  }
}

/**
 * بطاقات عروض الأسعار.
 *
 * من الصفحة المعروضة لا من كل العروض: لا مسار إحصاءٍ لها في الخادم، وعدُّ
 * ما يُعرض أصدق من رقمٍ يوهم بشمولٍ لا يملكه — ولذلك تقول البطاقة «في هذه
 * الصفحة» صراحةً. والعدد الإجمالي وحده يأتي من الخادم.
 */
function quotationCards(page: QuotationPage): RecordStat[] {
  const items = page.items;
  const sum = (rows: QuotationRow[]) =>
    rows.reduce((n, q) => n + Number(q.total), 0);
  const by = (st: string) => items.filter((q) => q.status === st);
  const accepted = by("ACCEPTED");
  const sent = by("SENT");
  /* المنتهي صلاحيته وغير المقبول: ما يحتاج تجديدًا أو إغلاقًا. */
  const expired = items.filter(
    (q) =>
      q.validUntil &&
      new Date(q.validUntil).getTime() < Date.now() &&
      q.status !== "ACCEPTED",
  );

  return [
    {
      key: "count",
      label: "عروض (إجمالًا)",
      group: "العدد",
      icon: "receipt",
      color: "#3672EA",
      value: page.total,
      numeric: true,
      note: `${items.length} في هذه الصفحة`,
    },
    {
      key: "value",
      label: "قيمة هذه الصفحة",
      group: "القيمة",
      icon: "wallet",
      color: "#1D46A6",
      value: money(sum(items)),
      numeric: true,
    },
    {
      key: "sent",
      label: "مُرسَلة",
      group: "العدد",
      icon: "send",
      color: "#5596FF",
      value: sent.length,
      numeric: true,
      note: money(sum(sent)),
    },
    {
      key: "accepted",
      label: "مقبولة",
      group: "العدد",
      icon: "check",
      color: "#51A521",
      value: accepted.length,
      numeric: true,
      note: money(sum(accepted)),
    },
    {
      key: "expired",
      label: "منتهية الصلاحية",
      group: "المتابعة",
      icon: "alert",
      color: expired.length ? "#DC2626" : "#9BA3AE",
      value: expired.length,
      numeric: true,
      note: expired.length ? "تحتاج تجديدًا" : "لا شيء منتهٍ",
    },
    {
      key: "avg",
      label: "متوسط قيمة العرض",
      group: "القيمة",
      icon: "chartBar",
      color: "#8791A1",
      value: items.length ? money(Math.round(sum(items) / items.length)) : "—",
      numeric: true,
    },
  ];
}
